import { closestCenter, DndContext, KeyboardSensor, MouseSensor, useSensor, useSensors, type DragEndEvent, type Modifier } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  asStringArray,
  exportCollectionCSV,
  exportCollectionJSON,
  SYSTEM_KEYS,
  uid,
  type CollectionData,
  type FieldDef,
  type FilterOp,
  type FilterRule,
  type RecordData,
  type SortRule,
  type ViewDef,
  type ViewType,
} from '@tomo/core';
import {
  Avatar,
  Button,
  ColorDot,
  ContextMenu,
  IconButton,
  Input,
  Menu,
  Popover,
  SearchList,
  SegmentedControl,
  Select,
  Switch,
  useConfirm,
  type MenuItem,
} from '@tomo/ui';
import {
  ArrowDownNarrowWide,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  Braces,
  Columns3,
  Copy,
  Eye,
  EyeOff,
  FileSpreadsheet,
  Filter,
  Image as ImageIcon,
  LayoutGrid,
  List,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Table2,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useAllRecords, usePeople, useProject, useReadOnly } from '../../data/hooks.tsx';
import { downloadText, slugify } from '../../lib/files.ts';
import { useHotkey } from '../../lib/hotkeys.ts';
import { Icon } from '../../lib/icons.tsx';
import { FieldTypeIcon } from '../fields/display.tsx';
import { groupableFields, NO_VALUE_OPS, OP_LABEL, opsFor, VIEW_TYPE_LABEL } from './view-utils.ts';

export const VIEW_ICON: Record<ViewType, ReactNode> = {
  table: <Table2 />,
  board: <Columns3 />,
  gallery: <LayoutGrid />,
  list: <List />,
};

export interface ViewToolbarProps {
  collection: CollectionData;
  view: ViewDef;
  onSelectView: (viewId: string) => void;
  search: string;
  onSearch: (s: string) => void;
  visible: RecordData[];
}

// A tab being dragged stays in the strip, like the browser tab bar it looks like.
const horizontalOnly: Modifier = ({ transform }) => ({ ...transform, y: 0 });

export function ViewToolbar({ collection, view, onSelectView, search, onSearch, visible }: ViewToolbarProps) {
  const project = useProject();
  const confirm = useConfirm();
  // Viewers can search and export; views are shared configuration, so they can't reshape them.
  const readOnly = useReadOnly();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  useHotkey('/', () => setSearchOpen(true));
  const update = (patch: Partial<ViewDef>) => project.updateView(collection.id, view.id, patch);

  const viewIds = collection.views.map((v) => v.id);
  const sensors = useSensors(
    // Mouse only: on touch screens a drag would fight the strip's own scrolling, and the menu can move tabs.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Space picks a tab up (Enter still switches to it).
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] } }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) project.moveView(collection.id, String(active.id), viewIds.indexOf(String(over.id)));
  };

  const addView = (type: ViewType) => {
    const status = collection.fields.find((f) => f.role === 'status') ?? groupableFields(collection)[0];

    const id = project.addView(collection.id, {
      name: VIEW_TYPE_LABEL[type],
      type,
      groupBy: type === 'board' ? (status?.id ?? null) : null,
      // null means the record's own cover, which every record can have.
      coverFieldId: null,
    });
    onSelectView(id);
  };

  const viewMenu = (v: ViewDef): MenuItem[] => {
    const i = collection.views.findIndex((x) => x.id === v.id);
    return [
      { label: 'Rename view', icon: <Pencil />, onSelect: () => setRenaming(v.id) },
      {
        label: 'Duplicate view',
        icon: <Copy />,
        onSelect: () => {
          const id = project.duplicateView(collection.id, v.id);
          if (id) onSelectView(id);
        },
      },
      { type: 'separator' },
      { label: 'Move left', icon: <ArrowLeft />, disabled: i === 0, onSelect: () => project.moveView(collection.id, v.id, i - 1) },
      { label: 'Move right', icon: <ArrowRight />, disabled: i === collection.views.length - 1, onSelect: () => project.moveView(collection.id, v.id, i + 1) },
      { type: 'separator' },
      {
        label: 'Delete view',
        icon: <Trash2 />,
        danger: true,
        disabled: collection.views.length <= 1,
        onSelect: async () => {
          if (await confirm({ title: `Delete the “${v.name}” view?`, description: 'Only the view is removed — records are untouched.', confirmLabel: 'Delete view', danger: true })) {
            project.removeView(collection.id, v.id);
            if (v.id === view.id) onSelectView(collection.views.find((x) => x.id !== v.id)!.id);
          }
        },
      },
    ];
  };

  return (
    <div className="viewbar">
      <div className="viewbar__tabs" role="tablist">
        {readOnly ? (
          collection.views.map((v) => (
            <button key={v.id} type="button" role="tab" aria-selected={v.id === view.id} className="viewbar__tab" onClick={() => onSelectView(v.id)}>
              {v.name}
            </button>
          ))
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[horizontalOnly]} onDragEnd={onDragEnd}>
            <SortableContext items={viewIds} strategy={horizontalListSortingStrategy}>
              {collection.views.map((v) => (
                <ViewTab
                  key={v.id}
                  view={v}
                  selected={v.id === view.id}
                  renaming={renaming === v.id}
                  menu={viewMenu(v)}
                  onSelect={() => onSelectView(v.id)}
                  onRename={() => setRenaming(v.id)}
                  onRenameDone={(name) => {
                    if (name && name !== v.name) project.updateView(collection.id, v.id, { name });
                    setRenaming(null);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
        {!readOnly && (
          <Menu
            trigger={
              <IconButton label="Add view" size="sm">
                <Plus />
              </IconButton>
            }
            items={(['table', 'board', 'gallery', 'list'] as ViewType[]).map((t) => ({ label: `${VIEW_TYPE_LABEL[t]} view`, icon: VIEW_ICON[t], onSelect: () => addView(t) }))}
          />
        )}
      </div>

      {/* Quiet tools: icons only, with a count on Filter and Sort while they're in use. */}
      <div className="viewbar__tools">
        {searchOpen || search ? (
          <div className="viewbar__search">
            <Input
              size="sm"
              variant="ghost"
              autoFocus
              leading={<Search />}
              placeholder="Search"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              onBlur={() => !search && setSearchOpen(false)}
              trailing={
                search ? (
                  <button type="button" aria-label="Clear search" onClick={() => onSearch('')} style={{ display: 'inline-flex' }}>
                    <X size={13} />
                  </button>
                ) : undefined
              }
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return;
                onSearch('');
                setSearchOpen(false);
              }}
            />
          </div>
        ) : (
          <IconButton label="Search" size="sm" tooltipShortcut="/" onClick={() => setSearchOpen(true)}>
            <Search />
          </IconButton>
        )}
        {!readOnly && <FilterPopover collection={collection} view={view} onChange={(filters, filterMode) => update({ filters, filterMode })} />}
        {!readOnly && <SortPopover collection={collection} view={view} onChange={(sort) => update({ sort })} />}
        {view.type === 'board' && !readOnly && (
          <Select
            size="sm"
            variant="ghost"
            value={view.groupBy ?? ''}
            placeholder="Group by…"
            renderValue={(o) => <>Group: {o.label}</>}
            onChange={(groupBy) => update({ groupBy })}
            options={groupableFields(collection).map((f) => ({ value: f.id, label: f.name, icon: <FieldTypeIcon type={f.type} /> }))}
          />
        )}
        {!readOnly && <FieldsPopover collection={collection} view={view} onChange={(hiddenFields) => update({ hiddenFields })} />}
        <Menu
          placement="bottom-end"
          trigger={
            <IconButton label="View options" size="sm">
              <MoreHorizontal />
            </IconButton>
          }
          items={() => {
            const coverFields = collection.fields.filter((f) => f.type === 'file' || f.type === 'relation');
            const items: MenuItem[] = [];
            if (!readOnly && (view.type === 'gallery' || view.type === 'board')) {
              items.push({ type: 'label', label: 'Card size' });
              for (const s of ['sm', 'md', 'lg'] as const) {
                items.push({ label: s === 'sm' ? 'Small' : s === 'md' ? 'Medium' : 'Large', checked: view.cardSize === s, keepOpen: true, onSelect: () => update({ cardSize: s }) });
              }
              items.push({ type: 'label', label: 'Card cover' });
              items.push({ label: 'Record cover', checked: !view.coverFieldId, icon: <ImageIcon />, onSelect: () => update({ coverFieldId: null }) });
              for (const f of coverFields) items.push({ label: f.name, icon: <ImageIcon />, checked: view.coverFieldId === f.id, onSelect: () => update({ coverFieldId: f.id }) });
              items.push({ type: 'separator' });
            }
            const base = slugify(collection.name);
            items.push(
              { type: 'label', label: `Export ${visible.length} record${visible.length === 1 ? '' : 's'}` },
              {
                label: 'JSON for game engines',
                icon: <Braces />,
                hint: 'camelCase',
                onSelect: () => downloadText(JSON.stringify(exportCollectionJSON(project, collection.id, { keyStyle: 'camelCase' }, visible), null, 2), `${base}.json`, 'application/json'),
              },
              {
                label: 'JSON (field names)',
                icon: <Braces />,
                onSelect: () => downloadText(JSON.stringify(exportCollectionJSON(project, collection.id, { keyStyle: 'name' }, visible), null, 2), `${base}.json`, 'application/json'),
              },
              { label: 'CSV spreadsheet', icon: <FileSpreadsheet />, onSelect: () => downloadText(exportCollectionCSV(project, collection.id, visible), `${base}.csv`, 'text/csv') },
            );
            if (!readOnly) items.push({ type: 'separator' }, ...viewMenu(view));
            return items;
          }}
        />
      </div>
    </div>
  );
}

/** One view tab: click to switch, double-click to rename, drag (or Space, then arrows) to reorder. */
function ViewTab({
  view,
  selected,
  renaming,
  menu,
  onSelect,
  onRename,
  onRenameDone,
}: {
  view: ViewDef;
  selected: boolean;
  renaming: boolean;
  menu: MenuItem[];
  onSelect: () => void;
  onRename: () => void;
  onRenameDone: (name: string | null) => void;
}) {
  // Keep tab semantics: the tab still switches the view, and can also be picked up.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: view.id, disabled: renaming, attributes: { role: 'tab' } });

  if (renaming) {
    return (
      <input
        className="viewbar__rename"
        autoFocus
        defaultValue={view.name}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => onRenameDone(e.currentTarget.value.trim())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') onRenameDone(null);
        }}
      />
    );
  }

  return (
    <ContextMenu items={menu}>
      <button
        ref={setNodeRef}
        type="button"
        aria-selected={selected}
        className="viewbar__tab"
        data-dragging={isDragging || undefined}
        style={{ transform: CSS.Translate.toString(transform), transition }}
        onClick={onSelect}
        onDoubleClick={onRename}
        {...attributes}
        {...listeners}
      >
        {view.name}
      </button>
    </ContextMenu>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Filters
 * ---------------------------------------------------------------------------------------------- */

function FilterPopover({ collection, view, onChange }: { collection: CollectionData; view: ViewDef; onChange: (filters: FilterRule[], mode: 'and' | 'or') => void }) {
  const fields = collection.fields;
  const active = view.filters.length;
  const set = (id: string, patch: Partial<FilterRule>) => onChange(view.filters.map((f) => (f.id === id ? { ...f, ...patch } : f)), view.filterMode);
  const add = () => {
    const field = fields.find((f) => f.type !== 'file') ?? null;
    const op = field ? opsFor(field)[0]! : 'contains';
    onChange([...view.filters, { id: uid(8), fieldId: field?.id ?? SYSTEM_KEYS.title, op }], view.filterMode);
  };
  return (
    <Popover
      width={560}
      padding="md"
      placement="bottom-end"
      trigger={
        <IconButton label={active ? `Filter (${active})` : 'Filter'} size="sm" className="viewbar__tool" data-count={active || undefined}>
          <Filter />
        </IconButton>
      }
    >
      <div className="rules">
        {view.filters.length === 0 && <p className="rules__empty">No filters. Show only records that match conditions — e.g. Tier is T3, HP ≥ 1000.</p>}
        {view.filters.map((rule, i) => {
          const field = fields.find((f) => f.id === rule.fieldId);
          return (
            <div key={rule.id} className="rules__row">
              <span className="rules__lead">
                {i === 0 ? (
                  'Where'
                ) : i === 1 ? (
                  <Select
                    size="sm"
                    variant="ghost"
                    value={view.filterMode}
                    onChange={(m) => onChange(view.filters, m)}
                    options={[
                      { value: 'and', label: 'and' },
                      { value: 'or', label: 'or' },
                    ]}
                  />
                ) : (
                  view.filterMode
                )}
              </span>
              <Select
                size="sm"
                value={rule.fieldId}
                minWidth={200}
                onChange={(fieldId) => {
                  const f = fields.find((x) => x.id === fieldId);
                  set(rule.id, { fieldId, op: opsFor(f)[0]!, value: undefined });
                }}
                options={[
                  { value: SYSTEM_KEYS.title, label: collection.titleLabel, icon: <FieldTypeIcon type="text" /> },
                  ...fields.filter((f) => f.type !== 'file').map((f) => ({ value: f.id, label: f.name, icon: <FieldTypeIcon type={f.type} /> })),
                ]}
              />
              <Select size="sm" value={rule.op} onChange={(op) => set(rule.id, { op: op as FilterOp, ...(NO_VALUE_OPS.has(op as FilterOp) ? { value: undefined } : {}) })} options={opsFor(field).map((op) => ({ value: op, label: OP_LABEL[op] }))} />
              <div className="rules__value">{!NO_VALUE_OPS.has(rule.op) && <FilterValue field={field} rule={rule} onChange={(value) => set(rule.id, { value })} />}</div>
              <IconButton label="Remove filter" size="xs" tooltip={false} onClick={() => onChange(view.filters.filter((f) => f.id !== rule.id), view.filterMode)}>
                <X />
              </IconButton>
            </div>
          );
        })}
        <div className="rules__foot">
          <Button size="sm" variant="ghost" icon={<Plus />} onClick={add}>
            Add filter
          </Button>
          {active > 0 && (
            <Button size="sm" variant="ghost" onClick={() => onChange([], 'and')} style={{ marginLeft: 'auto' }}>
              Clear all
            </Button>
          )}
        </div>
      </div>
    </Popover>
  );
}

function FilterValue({ field, rule, onChange }: { field: FieldDef | undefined; rule: FilterRule; onChange: (v: unknown) => void }) {
  const people = usePeople();
  const records = useAllRecords();
  if (!field || ['text', 'url', 'dice'].includes(field.type)) {
    return <Input size="sm" placeholder="Value" defaultValue={typeof rule.value === 'string' ? rule.value : ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (['number', 'rating', 'range', 'formula', 'votes'].includes(field.type)) {
    return <Input size="sm" inputMode="decimal" placeholder="Number" defaultValue={rule.value === undefined ? '' : String(rule.value)} onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />;
  }
  if (field.type === 'date') {
    return <Input size="sm" type="date" defaultValue={typeof rule.value === 'string' ? rule.value : ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'select' && (rule.op === 'is' || rule.op === 'isNot')) {
    return (
      <Select
        size="sm"
        value={typeof rule.value === 'string' ? rule.value : null}
        placeholder="Option…"
        onChange={onChange}
        options={(field.config.options ?? []).map((o) => ({ value: o.id, label: o.label, icon: <ColorDot color={o.color} /> }))}
      />
    );
  }
  // multi-value pickers: tags, people, relations
  const items =
    field.type === 'person'
      ? people.map((p) => ({ id: p.id, label: p.name, icon: <Avatar name={p.name} color={p.color} size={16} /> }))
      : field.type === 'relation'
        ? records.filter((r) => !field.config.targetCollectionId || r.collectionId === field.config.targetCollectionId).map((r) => ({ id: r.id, label: r.title || 'Untitled', icon: <Icon token={r.icon} size={14} fallback="i:link" /> }))
        : (field.config.options ?? []).map((o) => ({ id: o.id, label: o.label, icon: <ColorDot color={o.color} /> }));
  const selected = asStringArray(rule.value);
  const label = selected.length ? items.filter((i) => selected.includes(i.id)).map((i) => i.label).join(', ') : 'Choose…';
  return (
    <Popover
      width={260}
      trigger={
        <button type="button" className="g-select" data-size="sm" style={{ width: '100%' }}>
          <span className="g-select__value">{label}</span>
        </button>
      }
    >
      <SearchList items={items} selectedIds={selected} showCheck onSelect={(it) => onChange(selected.includes(it.id) ? selected.filter((x) => x !== it.id) : [...selected, it.id])} />
    </Popover>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Sorts
 * ---------------------------------------------------------------------------------------------- */

function SortPopover({ collection, view, onChange }: { collection: CollectionData; view: ViewDef; onChange: (sort: SortRule[]) => void }) {
  const options = [
    { value: SYSTEM_KEYS.title, label: collection.titleLabel, icon: <FieldTypeIcon type="text" /> },
    ...collection.fields.filter((f) => f.type !== 'file').map((f) => ({ value: f.id, label: f.name, icon: <FieldTypeIcon type={f.type} /> })),
    { value: SYSTEM_KEYS.created, label: 'Created', icon: <ArrowDownNarrowWide /> },
    { value: SYSTEM_KEYS.updated, label: 'Last edited', icon: <ArrowDownNarrowWide /> },
  ];
  const active = view.sort.length;
  return (
    <Popover
      width={420}
      padding="md"
      placement="bottom-end"
      trigger={
        <IconButton label={active ? `Sort (${active})` : 'Sort'} size="sm" className="viewbar__tool" data-count={active || undefined}>
          <ArrowUpDown />
        </IconButton>
      }
    >
      <div className="rules">
        {view.sort.length === 0 && <p className="rules__empty">Manual order. Add a sort to order by any field — e.g. DPS, highest first.</p>}
        {view.sort.map((s, i) => (
          <div key={`${s.fieldId}-${i}`} className="rules__row">
            <Select size="sm" value={s.fieldId} minWidth={200} options={options} onChange={(fieldId) => onChange(view.sort.map((x, j) => (j === i ? { ...x, fieldId } : x)))} />
            <SegmentedControl
              size="sm"
              value={s.dir}
              onChange={(dir) => onChange(view.sort.map((x, j) => (j === i ? { ...x, dir } : x)))}
              options={[
                { value: 'asc', label: 'Ascending' },
                { value: 'desc', label: 'Descending' },
              ]}
            />
            <IconButton label="Remove sort" size="xs" tooltip={false} onClick={() => onChange(view.sort.filter((_, j) => j !== i))}>
              <X />
            </IconButton>
          </div>
        ))}
        <div className="rules__foot">
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus />}
            onClick={() => {
              const used = new Set(view.sort.map((s) => s.fieldId));
              const next = options.find((o) => !used.has(o.value));
              if (next) onChange([...view.sort, { fieldId: next.value, dir: 'asc' }]);
            }}
          >
            Add sort
          </Button>
          {active > 0 && (
            <Button size="sm" variant="ghost" onClick={() => onChange([])} style={{ marginLeft: 'auto' }}>
              Manual order
            </Button>
          )}
        </div>
      </div>
    </Popover>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Field visibility
 * ---------------------------------------------------------------------------------------------- */

function FieldsPopover({ collection, view, onChange }: { collection: CollectionData; view: ViewDef; onChange: (hidden: string[]) => void }) {
  const hidden = useMemo(() => new Set(view.hiddenFields), [view.hiddenFields]);
  const shownCount = collection.fields.length - collection.fields.filter((f) => hidden.has(f.id)).length;
  return (
    <Popover
      width={280}
      padding="sm"
      placement="bottom-end"
      trigger={
        <IconButton label={`Fields (${shownCount} of ${collection.fields.length} shown)`} size="sm" className="viewbar__tool">
          <SlidersHorizontal />
        </IconButton>
      }
    >
      <div className="fields-pop">
        <div className="fields-pop__head">
          <span className="g-overline">Shown in this view</span>
          <button type="button" className="inline-link" style={{ fontSize: 12 }} onClick={() => onChange(hidden.size ? [] : collection.fields.map((f) => f.id))}>
            {hidden.size ? 'Show all' : 'Hide all'}
          </button>
        </div>
        {collection.fields.map((f) => {
          const on = !hidden.has(f.id);
          return (
            <label key={f.id} className="fields-pop__row">
              <FieldTypeIcon type={f.type} />
              <span className="g-truncate" style={{ flex: 1 }}>
                {f.name}
              </span>
              {on ? <Eye size={14} className="subtle" /> : <EyeOff size={14} className="subtle" />}
              <Switch size="sm" checked={on} onCheckedChange={(v) => onChange(v ? view.hiddenFields.filter((h) => h !== f.id) : [...view.hiddenFields, f.id])} aria-label={`Show ${f.name}`} />
            </label>
          );
        })}
      </div>
    </Popover>
  );
}
